import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronLeft,
  FileJson,
  Folder,
  Plus,
  Search,
  Trash2,
  Copy,
  FolderOpen,
  Filter,
  Home,
} from "lucide-react";
import { localizationClient } from "../../api/v2/localizationClient";
import { RepositoryTreeNode, WorkspaceBinding } from "../../api/v2/types";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "../../components/ui/context-menu";
import { parseApiError } from "../../api/http/errors";
import InlineHint from "../shared/InlineHint";
import SectionState from "../shared/SectionState";
import HelpHoverCard from "../shared/HelpHoverCard";
import { Spinner } from "../../components/ui/spinner";

interface TreePickerDialogProps {
  owner: string;
  repo: string;
  workspaceKey: string;
  defaultBranch?: string;
  selectedBindings: WorkspaceBinding[];
  onChangeBindings: (bindings: WorkspaceBinding[]) => void;
}

const TreePickerDialog = ({
  owner,
  repo,
  workspaceKey,
  defaultBranch,
  selectedBindings,
  onChangeBindings,
}: TreePickerDialogProps) => {
  const [open, setOpen] = useState(false);
  const [nodes, setNodes] = useState<RepositoryTreeNode[]>([]);
  const [search, setSearch] = useState("");
  const [onlyJson, setOnlyJson] = useState(true);
  const [path, setPath] = useState("");
  const [errorText, setErrorText] = useState("");
  const [quickSearch, setQuickSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const selectedMap = useMemo(
    () => new Map(selectedBindings.map((binding) => [binding.filePath, binding])),
    [selectedBindings]
  );

  const loadTree = useCallback(
    async (refresh = false) => {
      setIsLoading(true);
      try {
        const response = await localizationClient.getRepositoryTree(owner, repo, {
          workspaceKey,
          ref: defaultBranch,
          path,
          onlyJson,
          search: debouncedSearch,
          limit: 200,
          refresh,
        });
        setNodes(response.nodes);
      } catch (error) {
        setErrorText(parseApiError(error).message);
      } finally {
        setIsLoading(false);
      }
    },
    [owner, repo, workspaceKey, defaultBranch, path, onlyJson, debouncedSearch]
  );

  const runQuickSearch = async () => {
    if (!quickSearch.trim()) return;
    setIsLoading(true);
    try {
      const response = await localizationClient.searchRepositoryFiles(owner, repo, {
        workspaceKey,
        ref: defaultBranch,
        search: quickSearch,
        limit: 100,
      });
      setNodes(response.nodes);
    } catch (error) {
      setErrorText(parseApiError(error).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadTree();
  }, [open, loadTree]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const addFile = (filePath: string) => {
    if (selectedMap.has(filePath)) return;
    onChangeBindings([
      ...selectedBindings,
      { filePath, language: "", group: "default" },
    ]);
  };

  const removeFile = (filePath: string) => {
    onChangeBindings(selectedBindings.filter((binding) => binding.filePath !== filePath));
  };

  const openDirectory = (nextPath: string) => {
    setPath(nextPath);
    setSearch("");
  };

  const goToParentDirectory = () => {
    if (!path) return;
    const parts = path.split("/").filter(Boolean);
    parts.pop();
    openDirectory(parts.join("/"));
  };

  const breadcrumbs = useMemo(() => path.split("/").filter(Boolean), [path]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <FolderOpen className="h-4 w-4 mr-1" /> Open File Picker
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-screen-2xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>Repository tree picker</DialogTitle>
          <DialogDescription>
            Browse tree, apply filters, select JSON files and assign language/group.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 overflow-hidden">
          <div className="border rounded p-3 space-y-3 overflow-auto">
            <div className="flex gap-2">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search in current tree scope"
              />
              <Button onClick={() => loadTree()} disabled={isLoading}>
                {isLoading ? <Spinner className="h-4 w-4 mr-1" /> : <Search className="h-4 w-4 mr-1" />} Search
              </Button>
              <InlineHint text="Search works inside current path scope. Use Global file search for repository-wide lookup." />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={goToParentDirectory} disabled={!path || isLoading}>
                <ChevronLeft className="h-4 w-4 mr-1" /> Up
              </Button>
              <Input value={path} onChange={(event) => setPath(event.target.value)} placeholder="Path filter" disabled={isLoading} />
              <Button variant="outline" onClick={() => setOnlyJson((prev) => !prev)} disabled={isLoading}>
                <Filter className="h-4 w-4 mr-1" /> {onlyJson ? "JSON only" : "All files"}
              </Button>
              <Button variant="outline" onClick={() => setPath("")} disabled={isLoading}>
                <Home className="h-4 w-4 mr-1" /> Root
              </Button>
              <Button variant="outline" onClick={() => loadTree(true)} disabled={isLoading}>
                Refresh
              </Button>
            </div>
            <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
              <button className="underline" onClick={() => openDirectory("")}>
                root
              </button>
              {breadcrumbs.map((segment, index) => {
                const segPath = breadcrumbs.slice(0, index + 1).join("/");
                return (
                  <React.Fragment key={segPath}>
                    <span>/</span>
                    <button className="underline" onClick={() => openDirectory(segPath)}>
                      {segment}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
            <div className="flex gap-2">
              <Input
                value={quickSearch}
                onChange={(event) => setQuickSearch(event.target.value)}
                placeholder="Global file search"
              />
              <Button variant="outline" onClick={runQuickSearch} disabled={isLoading || !quickSearch.trim()}>
                Search files
              </Button>
            </div>
            <HelpHoverCard
              title="Tree picker tips"
              description="Left panel shows current path scope. Right click for context actions. Directory click opens nested tree."
            />
            <SectionState
              isLoading={isLoading}
              errorText={errorText}
              isEmpty={nodes.length === 0}
              emptyText="No files found for current filters. Try clearing search or switching to root."
              minHeightClassName="min-h-[360px]"
            >
            <div className="space-y-1">
              {nodes.map((node) => {
                const isFile = node.type === "file";
                return (
                  <ContextMenu key={node.path}>
                    <ContextMenuTrigger asChild>
                      <div
                        className="border rounded px-2 py-1 flex items-center justify-between cursor-pointer hover:bg-accent/40"
                        onClick={() => {
                          if (!isFile) {
                            openDirectory(node.path);
                          }
                        }}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isFile ? (
                            <FileJson className="h-4 w-4 shrink-0" />
                          ) : (
                            <Folder className="h-4 w-4 shrink-0" />
                          )}
                          <span className="text-sm truncate" title={node.path}>
                            {node.name}
                          </span>
                        </div>
                        {isFile ? (
                          <Button size="sm" variant="ghost" onClick={() => addFile(node.path)}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      {isFile ? (
                        <ContextMenuItem onClick={() => addFile(node.path)}>
                          Add to selection
                        </ContextMenuItem>
                      ) : (
                        <ContextMenuItem onClick={() => openDirectory(node.path)}>Open folder</ContextMenuItem>
                      )}
                      <ContextMenuItem onClick={() => navigator.clipboard?.writeText(node.path)}>
                        <Copy className="h-4 w-4 mr-2" /> Copy path
                      </ContextMenuItem>
                      {!isFile ? <ContextMenuItem onClick={() => openDirectory(node.path)}>Filter by folder</ContextMenuItem> : null}
                      {selectedMap.has(node.path) ? (
                        <ContextMenuItem onClick={() => removeFile(node.path)}>
                          Remove from selection
                        </ContextMenuItem>
                      ) : null}
                    </ContextMenuContent>
                  </ContextMenu>
                );
              })}
            </div>
            </SectionState>
          </div>

          <div className="border rounded p-3 space-y-2 overflow-auto">
            <h4 className="font-medium">Selected files</h4>
            {selectedBindings.map((binding) => (
              <div key={binding.filePath} className="border rounded p-2 space-y-2">
                <p className="text-xs break-all">{binding.filePath}</p>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => removeFile(binding.filePath)}>
                    <Trash2 className="h-4 w-4 mr-1" /> Remove
                  </Button>
                </div>
              </div>
            ))}
            <Button onClick={() => setOpen(false)}>
              <Check className="h-4 w-4 mr-1" /> Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TreePickerDialog;
